import { useId, type InputHTMLAttributes, type ReactNode } from 'react';

import { cn } from '@/shared/utils/cn';

interface FormFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  error?: string | undefined;
  hint?: ReactNode;
}

/**
 * Label, input, hint and error as one unit.
 *
 * Keeping them together is what makes the accessible wiring reliable: the error
 * is announced, referenced by `aria-describedby`, and cannot be forgotten at a
 * single call site.
 */
export function FormField({ label, error, hint, className, ...rest }: FormFieldProps) {
  const inputId = useId();
  const errorId = `${inputId}-error`;
  const hintId = `${inputId}-hint`;
  const hasError = error !== undefined && error !== '';

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={inputId} className="text-fg text-sm font-medium">
        {label}
      </label>

      <input
        id={inputId}
        aria-invalid={hasError}
        aria-describedby={cn(hasError && errorId, hint !== undefined && hintId) || undefined}
        className={cn(
          'bg-surface-raised border-line text-fg placeholder:text-fg-subtle touch-target',
          'rounded-lg border px-4 py-3 text-base transition-colors',
          'focus:border-brand focus:outline-none',
          hasError && 'border-danger',
          className,
        )}
        {...rest}
      />

      {hint !== undefined && (
        <p id={hintId} className="text-fg-subtle text-xs">
          {hint}
        </p>
      )}

      {hasError && (
        <p id={errorId} role="alert" className="text-danger text-xs">
          {error}
        </p>
      )}
    </div>
  );
}
